import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CaseStudyVersion } from '@/types/case-study';
import { formatDistanceToNow } from 'date-fns';

interface RestoreVersionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  version: CaseStudyVersion | null;
  onRestore: (version: CaseStudyVersion) => Promise<void>;
  isRestoring?: boolean;
}

export function RestoreVersionDialog({
  open,
  onOpenChange,
  version,
  onRestore,
  isRestoring = false,
}: RestoreVersionDialogProps) {
  const handleRestore = async () => {
    if (!version) return;

    try {
      await onRestore(version);
      onOpenChange(false);
    } catch (error) {
      // Error handling is done by parent component
      console.error('[RestoreVersionDialog] Restore error:', error);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  if (!version) return null;

  const versionLabel = version.label || `Version ${version.version_number}`;
  const createdAt = formatDistanceToNow(new Date(version.created_at), { addSuffix: true });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-lg p-8 rounded-md">
        <DialogHeader>
          <DialogTitle>Restore Version?</DialogTitle>
          <DialogDescription>
            This will replace your current case study with the selected version.
            Any unsaved changes will be lost.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="space-y-2">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">
                    {versionLabel}
                  </h4>
                  <p className="text-sm text-gray-600">
                    Version {version.version_number}
                  </p>
                </div>
                {version.version_type === 'auto_publish' && (
                  <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                    Auto-saved
                  </span>
                )}
              </div>

              <p className="text-sm text-gray-500">
                Created {createdAt}
              </p>

              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-700 mb-1">
                  This version contains:
                </p>
                <ul className="text-xs text-gray-600 space-y-0.5">
                  <li>• Title: {version.snapshot.title || 'Untitled'}</li>
                  <li>• {version.snapshot.sections.length} section{version.snapshot.sections.length !== 1 ? 's' : ''}</li>
                  <li>• Status: {version.snapshot.status}</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-lg bg-yellow-50 border border-yellow-200 p-3">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> Your current undo/redo history will be cleared after restoring.
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isRestoring}
            className="w-40"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleRestore}
            disabled={isRestoring}
            className="w-40"
          >
            {isRestoring ? 'Restoring...' : 'Restore Version'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
